import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  useTheme,
  Paper,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import shopifyLogo from '../../assets/images/shopify logo.png';
import ProductCard from '../../components/common/ProductCard';
import CartSidebar from '../../components/common/CartSidebar';
import FloatingCartButton from '../../components/common/FloatingCartButton';

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '30vh',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  display: 'flex',
  alignItems: 'center',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
}));



const FeatureBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
}));

const HomeownerStorePage: React.FC = () => {
  const theme = useTheme();
  const [cartOpen, setCartOpen] = useState(false);
  
  // Newsletter form state
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);

  const handleCartOpen = () => {
    setCartOpen(true);
  };

  const handleCartClose = () => {
    setCartOpen(false);
  };

  const handleAddToCart = () => {
    // Automatically open cart sidebar to show the item was added
    setCartOpen(true);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setMessage('Please enter a valid email address');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('');
    setMessageType(null);

    try {
      const response = await fetch('/.netlify/functions/mailchimp-subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage(result.message);
        setMessageType('success');
        setEmail(''); // Clear the form
      } else {
        setMessage(result.error || 'Failed to subscribe');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h2" component="h1" sx={{ 
                fontWeight: 'bold', 
                mb: 3,
                fontSize: { xs: '2rem', md: '3rem' },
                lineHeight: 1.2,
              }}>
                HAVEN Homeowner Store
              </Typography>
              <Typography variant="h6" sx={{ 
                mb: 4,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                lineHeight: 1.6,
                maxWidth: '600px',
                opacity: 0.9,
              }}>
                Transform your home into a safe haven with our active air management system. Optimize IAQ with HAVEN sensors and automations, combined with your existing solutions.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Products Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h4"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{ mb: 6, fontWeight: 'bold' }}
        >
          Homeowner Products
        </Typography>

        <Grid container spacing={4}>
          {/* HAVEN Room Monitor - Real Shopify Product (CAROUSEL WITH AUTO-PLAY) */}
          <Grid item xs={12} sm={6} md={4}>
            <ProductCard 
              productId="7827872809099"
              onAddToCart={handleAddToCart}
              enableCarousel={true}
              autoPlay={true}
            />
          </Grid>

          {/* HAVEN Room Monitor Controller Bundle - Real Shopify Product (CAROUSEL WITHOUT AUTO-PLAY) */}
          <Grid item xs={12} sm={6} md={4}>
            <ProductCard 
              productId="7847604977803"
              onAddToCart={handleAddToCart}
              enableCarousel={true}
              autoPlay={false}
            />
          </Grid>

          {/* Professionally Installed HAVEN Central Air Bundle - Real Shopify Product (CAROUSEL WITHOUT AUTO-PLAY) */}
          <Grid item xs={12} sm={6} md={4}>
            <ProductCard 
              productId="7847606157451"
              onAddToCart={handleAddToCart}
              enableCarousel={true}
              autoPlay={false}
            />
          </Grid>

        </Grid>
      </Container>

      {/* Trust Indicators */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureBox>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <img 
                    src={shopifyLogo} 
                    alt="Shopify" 
                    style={{ 
                      height: '40px', 
                      width: 'auto'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Powered by Shopify
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Secure & trusted checkout
                </Typography>
              </FeatureBox>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureBox>
                <Box sx={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <SupportAgentIcon sx={{ fontSize: '2.5rem', color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Expert Support
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Installation & setup help
                </Typography>
              </FeatureBox>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureBox>
                <Box sx={{ 
                  fontSize: '2.5rem', 
                  color: theme.palette.primary.main, 
                  mb: 2,
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  1
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Year Warranty
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Full product coverage
                </Typography>
              </FeatureBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Newsletter Signup */}
      <Box sx={{ backgroundColor: theme.palette.primary.main, color: 'white', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
              Stay Updated
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
              Get notified about new products, special offers, and air quality tips.
            </Typography>
            
            <Box component="form" onSubmit={handleNewsletterSubmit} sx={{ maxWidth: 400, mx: 'auto' }}>
              <Paper sx={{ p: 1, display: 'flex', mb: 2 }}>
                <TextField
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  variant="standard"
                  disabled={isSubmitting}
                  sx={{ 
                    flex: 1,
                    '& .MuiInput-underline:before': { display: 'none' },
                    '& .MuiInput-underline:after': { display: 'none' },
                    '& .MuiInputBase-input': { 
                      px: 2,
                      fontSize: '1rem',
                    },
                  }}
                />
                <Button 
                  type="submit"
                  variant="contained" 
                  disabled={isSubmitting || !email.trim()}
                  sx={{ 
                    ml: 1, 
                    px: 3,
                    minWidth: 120,
                    backgroundColor: theme.palette.secondary.main,
                    color: 'black',
                    '&:hover': {
                      backgroundColor: theme.palette.secondary.dark,
                    }
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Subscribe'
                  )}
                </Button>
              </Paper>
              
              {message && (
                <Alert 
                  severity={messageType || 'info'} 
                  sx={{ 
                    maxWidth: 400, 
                    mx: 'auto',
                    '& .MuiAlert-message': {
                      fontSize: '0.9rem'
                    }
                  }}
                >
                  {message}
                </Alert>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Cart Sidebar */}
      <CartSidebar open={cartOpen} onClose={handleCartClose} />
      
      {/* Floating Cart Button */}
      <FloatingCartButton onClick={handleCartOpen} />
    </Box>
  );
};

export default HomeownerStorePage; 